'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { useCoolClubMemberQuery } from '@/hooks/react-query/club/use-club'
import { useIsLoggedIn, useUserQuery } from '@/hooks/react-query/user/use-user'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { LocalStorage } from '@/lib/utils/local-storage'
import { usePopupQueue } from '@/providers/popup-queue-provider'
import { useWidgetSettingData } from '@/providers/setting-provider'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect } from 'react'

const RANKING_LABEL: Record<string, string> = {
  'thanh-vien-moi': 'new-user',
  'hang-bac': 'silver-user',
  'hang-vang': 'golden-user',
  'hang-bach-kim': 'platinum-user',
}

const POPUP_ID = 'popup-setting'
const POPUP_PRIORITY = 100 // Higher priority than other popups

export const PopupSetting = () => {
  const { data: coolClubMember } = useCoolClubMemberQuery()
  const widgetSetting = useWidgetSettingData()
  const popupSetting = widgetSetting?.popup_setting
  const isLoggedIn = useIsLoggedIn()
  const { enqueuePopup, dequeuePopup } = usePopupQueue()

  const { data: user } = useUserQuery()

  // Handle popup close - using useCallback to prevent dependency changes on every render
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dequeuePopup(POPUP_ID)
        LocalStorage.set('SETTING_POPUP', true)
      }
    },
    [dequeuePopup]
  )

  useEffect(() => {
    if (!popupSetting?.is_show || LocalStorage.get('SETTING_POPUP')) return

    const userRankingLabel = RANKING_LABEL[coolClubMember?.ranking_slug || '']
    const showPopupType = popupSetting.type
    let isShowPopup = false

    switch (showPopupType) {
      case 'all':
        isShowPopup = true
        break

      case 'anonimus-user':
        if (!isLoggedIn) {
          isShowPopup = true
        }
        break

      case 'logged-in':
        if (isLoggedIn) {
          isShowPopup = true
        }
        break

      //TODO: update
      case 'custom':
        const includeShow = popupSetting.include_show || []
        const includeShowValues = includeShow.map((item) => item.value)

        if (!isLoggedIn && includeShowValues.includes('anonimus-user')) {
          isShowPopup = true
        } else if (
          isLoggedIn &&
          includeShowValues.includes('no-order') &&
          user?.new_offer_user
        ) {
          isShowPopup = true
        } else if (
          userRankingLabel &&
          includeShowValues.includes(userRankingLabel)
        ) {
          isShowPopup = true
        }
        break

      default:
        isShowPopup = false
        break
    }

    // If should show popup, add it to the queue with high priority
    if (isShowPopup) {
      enqueuePopup({
        id: POPUP_ID,
        priority: POPUP_PRIORITY,
        component: (
          <PopupSettingContent
            popupSetting={popupSetting}
            isOpen={true}
            onOpenChange={handleOpenChange}
          />
        ),
      })
    }
  }, [
    coolClubMember,
    isLoggedIn,
    popupSetting,
    user,
    enqueuePopup,
    handleOpenChange,
  ])

  return null // The actual popup is rendered by the PopupQueueProvider
}

interface PopupSettingContentProps {
  popupSetting: {
    link?: string
    image?: string
    mb_image?: string
  }
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

// Extracted the actual popup content to a separate component
const PopupSettingContent = ({
  popupSetting,
  isOpen,
  onOpenChange,
}: PopupSettingContentProps) => {
  return (
    <ResponsiveModal open={isOpen} onOpenChange={onOpenChange}>
      <ResponsiveModalContent
        side={'center'}
        className='left-4 right-4 z-[103] gap-0 rounded-xl border-none bg-light p-0'
        aria-describedby={'modal-popup-setting'}
        closeClassName='-right-3 -top-3'
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />
        <Link href={popupSetting?.link || '#'} className='block'>
          <picture>
            <source
              media='(min-width: 768px)'
              srcSet={ImageHelper.getImageUrl(
                popupSetting?.image || '',
                '550_623'
              )}
            />
            <Image
              src={ImageHelper.getImageUrl(
                popupSetting?.mb_image || '',
                '550_623'
              )}
              alt='Popup promotion'
              width={500}
              height={500}
              className='h-auto w-full rounded-xl'
            />
          </picture>
        </Link>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
